import { useCallback, useEffect, useRef, useState } from 'react';
import { recordTour } from './tourTracking';

/**
 * Controla los tours guiados de una pantalla.
 *
 * No arranca nada solo: el usuario los lanza desde el botón flotante. Una
 * pantalla puede ofrecer un recorrido completo y otros más cortos por tema; el
 * hook solo necesita el catálogo.
 *
 * @param {Array<{ id: string, name: string, steps: Array }>} tours
 */
const useTours = (tours) => {
  // id del tour en curso (null = ninguno) y paso actual.
  const [activeId, setActiveId] = useState(null);
  const [index, setIndex] = useState(0);

  const tour = tours.find((item) => item.id === activeId) || null;
  const total = tour?.steps?.length || 0;

  // Tour que estaba en curso. Los pasos se pueden usar de verdad (el recorte deja
  // libre el elemento resaltado), así que el usuario puede salir de la pantalla a
  // la que pertenece el tour; cuando eso pasa ya no está en el catálogo y hay que
  // recordarlo para registrar hasta dónde llegó.
  const lastTour = useRef(null);

  useEffect(() => {
    if (tour) {
      lastTour.current = tour;
    }
  }, [tour]);

  // El tour en curso dejó de ofrecerse: termina solo, como un "saltar".
  useEffect(() => {
    if (!activeId || tour) {
      return;
    }
    const left = lastTour.current;
    if (left) {
      recordTour({
        status: 'skipped',
        stepReached: index + 1,
        tourId: left.id,
        totalSteps: left.steps?.length || 0,
      });
    }
    setActiveId(null);
    setIndex(0);
  }, [activeId, index, tour]);

  const finish = useCallback((status, stepReached) => {
    if (tour) {
      recordTour({
        status,
        stepReached,
        tourId: tour.id,
        totalSteps: total,
      });
    }
    setActiveId(null);
    setIndex(0);
  }, [total, tour]);

  const start = useCallback((tourId) => {
    setIndex(0);
    setActiveId(tourId);
  }, []);

  const next = useCallback(() => {
    setIndex((prev) => {
      if (prev + 1 >= total) {
        finish('completed', total);
        return 0;
      }
      return prev + 1;
    });
  }, [finish, total]);

  const back = useCallback(() => {
    setIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const skip = useCallback(() => finish('skipped', index + 1), [finish, index]);

  return {
    active: Boolean(tour),
    back,
    index,
    next,
    skip,
    start,
    step: tour?.steps?.[index],
    total,
    tour,
  };
};

export default useTours;
